import { useState, useEffect } from 'react';
import { deviceController } from '../../controllers/deviceController';
import { RefreshCw as RefreshIcon } from 'lucide-react';

export const OSUpdatesPage = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(new Set());

  useEffect(() => {
    deviceController.getAll().then(r => {
      if (r.success) setDevices(r.data);
      setLoading(false);
    });
  }, []);

  const triggerUpdate = async (deviceId) => {
    setUpdating(prev => new Set([...prev, deviceId]));
    await new Promise(r => setTimeout(r, 2000));
    setUpdating(prev => { const n = new Set(prev); n.delete(deviceId); return n; });
  };

  const latestVersions = { Android: '14.0', iOS: '17.4', Windows: '11 23H2', macOS: '14.4' };

  const needsUpdate = (dev) => {
    const latest = latestVersions[dev.platform];
    return dev.osVersion && latest && dev.osVersion !== latest;
  };

  const s = {
    page: { padding: 0 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
    titleWrap: { display: 'flex', alignItems: 'center', gap: '12px' },
    icon: { width: '44px', height: '44px', borderRadius: '12px', backgroundColor: '#10b98120', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    title: { fontSize: '26px', fontWeight: '700', color: '#1e293b', margin: 0 },
    subtitle: { fontSize: '13px', color: '#64748b', marginTop: '2px' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '14px', marginBottom: '22px' },
    statCard: { backgroundColor: '#fff', padding: '18px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
    statLabel: { fontSize: '12px', color: '#64748b', marginBottom: '6px' },
    statVal: { fontSize: '26px', fontWeight: '700' },
    card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { backgroundColor: '#f8fafc', padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569', borderBottom: '1px solid #e2e8f0' },
    td: { padding: '12px 16px', borderBottom: '1px solid #f1f5f9', fontSize: '13px', color: '#1e293b' },
    badge: { display: 'inline-block', padding: '3px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: '600' },
    updateBtn: { padding: '5px 12px', backgroundColor: '#10b98115', color: '#10b981', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' },
  };

  const outdated = devices.filter(d => needsUpdate(d));

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.titleWrap}>
          <div style={s.icon}><RefreshIcon size={22} color="#10b981" /></div>
          <div><h1 style={s.title}>OS Updates</h1><div style={s.subtitle}>Manage and push OS updates to devices</div></div>
        </div>
        <button style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}
          onClick={() => outdated.forEach(d => triggerUpdate(d.id))}>
          <RefreshIcon size={15} /> Update All Outdated
        </button>
      </div>

      <div style={s.statsGrid}>
        {[['Total Devices', devices.length, '#64748b'], ['Up to Date', devices.length - outdated.length, '#10b981'], ['Need Update', outdated.length, '#f59e0b'], ['Updating Now', updating.size, '#3b82f6']].map(([l, v, c]) => (
          <div key={l} style={{ ...s.statCard, borderLeft: `3px solid ${c}` }}><div style={s.statLabel}>{l}</div><div style={{ ...s.statVal, color: c }}>{v}</div></div>
        ))}
      </div>

      <div style={s.card}>
        {loading ? <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading...</div> : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Device</th>
                <th style={s.th}>Platform</th>
                <th style={s.th}>Current Version</th>
                <th style={s.th}>Latest Version</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {devices.map(dev => {
                const latest = latestVersions[dev.platform] || '—';
                const upToDate = dev.osVersion === latest || !dev.osVersion;
                const isUpdating = updating.has(dev.id);
                return (
                  <tr key={dev.id}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                    <td style={s.td}><div style={{ fontWeight: '500' }}>{dev.deviceName}</div><div style={{ fontSize: '11px', color: '#64748b' }}>{dev.assignedTo || '—'}</div></td>
                    <td style={s.td}><span style={{ ...s.badge, backgroundColor: `${dev.getPlatformColor()}15`, color: dev.getPlatformColor() }}>{dev.platform}</span></td>
                    <td style={s.td}><code style={{ fontSize: '12px' }}>{dev.osVersion || 'Unknown'}</code></td>
                    <td style={s.td}><code style={{ fontSize: '12px', color: '#10b981' }}>{latest}</code></td>
                    <td style={s.td}>
                      {isUpdating ? <span style={{ ...s.badge, backgroundColor: '#3b82f615', color: '#3b82f6' }}>Updating...</span>
                        : upToDate ? <span style={{ ...s.badge, backgroundColor: '#10b98115', color: '#10b981' }}>Up to Date</span>
                          : <span style={{ ...s.badge, backgroundColor: '#f59e0b15', color: '#f59e0b' }}>Update Available</span>}
                    </td>
                    <td style={s.td}>
                      {!upToDate && !isUpdating && (
                        <button style={s.updateBtn} onClick={() => triggerUpdate(dev.id)}>
                          <RefreshIcon size={11} /> Push Update
                        </button>
                      )}
                      {isUpdating && <span style={{ fontSize: '12px', color: '#64748b' }}>In progress...</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
export default OSUpdatesPage;